import { useState } from 'react'
import { FileDownload } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedChronEvent } from '@/analytics/utils/medchron-analytics'
import { downloadMedChronAssetExcel } from '../fetch/download-med-chron-asset-excel'

interface MedChronAssetDownloadButtonProps {
  assetId: string
  compact?: boolean
}

/**
 * Downloads the living chronology as an Excel file.
 */
export default function MedChronAssetDownloadButton(props: MedChronAssetDownloadButtonProps) {
  const { assetId, compact = false } = props
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const title = isLoading ? 'Download in progress for Medical Chronology' : 'Download Medical Chronology'

  const handleDownload = async () => {
    if (isLoading) return
    setIsLoading(true)
    trackMedChronEvent(AnalyticsEvent.MedChronAssetDownloadClicked, { medChronAssetId: assetId })
    try {
      await downloadMedChronAssetExcel(assetId, 'Medical Chronology.xlsx')
    } catch (error) {
      console.error('Error downloading medical chronology Excel:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Medical Chronology download failed. Please try again.',
          durationMs: kToastDurationMediumMs,
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (compact) {
    return (
      <IconButton
        aria-label={title}
        color="primary"
        icon={<FileDownload />}
        loading={isLoading}
        onClick={handleDownload}
        size="lg"
        title={title}
        variant="plain"
      />
    )
  }

  return <IconButton aria-label={title} icon={<FileDownload />} loading={isLoading} onClick={handleDownload} size="sm" title={title} variant="outlined" />
}
